import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import Webcam from "react-webcam";
import workerAPI from "../api/workerAPI";
import attendanceAPI from "../api/attendanceAPI";
import UserDashboard from "./UserDashboard";
import { FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import { MdFaceRetouchingNatural } from "react-icons/md";

const WorkerFaceAttendance = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const [isModelsLoaded, setIsModelsLoaded]     = useState(false);
  const [loadingText, setLoadingText]           = useState("Loading face models…");
  const [detectionMessage, setDetectionMessage] = useState("");
  const [messageType, setMessageType]           = useState("");
  const [worker, setWorker]                     = useState(null);
  const [faceMatched, setFaceMatched]           = useState(false);
  const [scanning, setScanning]                 = useState(false);
  const [loadingLocation, setLoadingLocation]   = useState(false);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(`${MODEL_URL}/tiny_face_detector`),
          faceapi.nets.faceLandmark68Net.loadFromUri(`${MODEL_URL}/face_landmark_68`),
          faceapi.nets.faceRecognitionNet.loadFromUri(`${MODEL_URL}/face_recognition`),
        ]);
        setIsModelsLoaded(true);
        setLoadingText("Models loaded — ready to scan");
      } catch (err) {
        console.error(err);
        setLoadingText("❌ Model loading failed");
      }
    };

    loadModels();

    const saved = localStorage.getItem("worker");
    if (saved && saved !== "undefined") {
      setWorker(JSON.parse(saved));
    } else {
      setMsg("❌ No worker logged in", "error");
    }
  }, []);

  const setMsg = (text, type = "info") => {
    setDetectionMessage(text);
    setMessageType(type);
  };

  // ── Face verification ─────────────────────────────────────────────────────
  const handleVerifyFace = async () => {
    if (!isModelsLoaded) { setMsg("Models still loading, please wait…", "info"); return; }
    if (!worker)          { setMsg("❌ No worker logged in", "error"); return; }
    if (!webcamRef.current || webcamRef.current.video.readyState !== 4) {
      setMsg("Camera not ready, please wait…", "info"); return;
    }

    setScanning(true);
    setMsg("Detecting face…", "info");
    setFaceMatched(false);

    const video       = webcamRef.current.video;
    const displaySize = { width: video.videoWidth, height: video.videoHeight };

    faceapi.matchDimensions(canvasRef.current, displaySize);
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    try {
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setMsg("❌ No face detected. Please look directly at the camera.", "error");
        setScanning(false);
        return;
      }

      const resized = faceapi.resizeResults(detection, displaySize);
      faceapi.draw.drawDetections(canvasRef.current, resized);
      faceapi.draw.drawFaceLandmarks(canvasRef.current, resized);

      const res      = await workerAPI.get(`/${worker._id}`);
      const dbWorker = res.data;

      if (!dbWorker.faceDescriptor || !Array.isArray(dbWorker.faceDescriptor) || dbWorker.faceDescriptor.length === 0) {
        setMsg("❌ Face not registered. Contact admin.", "error");
        setScanning(false);
        return;
      }

      const labeledDescriptor = new faceapi.LabeledFaceDescriptors(
        dbWorker.username,
        [new Float32Array(dbWorker.faceDescriptor)]
      );

      const matcher = new faceapi.FaceMatcher([labeledDescriptor], 0.4);
      const match   = matcher.findBestMatch(detection.descriptor);

      if (match.label !== "unknown") {
        setFaceMatched(true);
        setMsg(`✅ Face matched — Welcome, ${match.label}!`, "success");
      } else {
        setFaceMatched(false);
        setMsg("❌ Face mismatch. Unauthorized user.", "error");
      }
    } catch (err) {
      console.error("Face verification error:", err);
      setMsg("❌ Face verification failed. Try again.", "error");
    } finally {
      setScanning(false);
    }
  };

  // ── Mark attendance — just fetch location and store it, no restriction ─────
  const markAttendance = async (action) => {
    if (!worker) { alert("No worker logged in."); return; }

    setLoadingLocation(true);
    setMsg("Getting your location…", "info");

    const sendAttendance = async (lat, lng) => {
      try {
        const res = await attendanceAPI.post("/mark", {
          workerId: worker._id,
          action,
          lat,
          lng,
        });
        setMsg(`✅ ${res.data.message}`, "success");
        alert(`✅ ${res.data.message}`);
        if (action === "logout") {
          setFaceMatched(false);
          setMsg("✅ Logged out. Scan again to login.", "success");
        }
      } catch (err) {
        const msg = err.response?.data?.message || "Attendance marking failed.";
        setMsg(`❌ ${msg}`, "error");
        alert(`❌ ${msg}`);
      } finally {
        setLoadingLocation(false);
      }
    };

    // Try to get location — if denied, still allow attendance with null coords
    if (!navigator.geolocation) {
      await sendAttendance(null, null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setMsg(`📍 Location acquired. Marking ${action}…`, "info");
        await sendAttendance(pos.coords.latitude, pos.coords.longitude);
      },
      async () => {
        // Location denied — still mark attendance, just without coords
        setMsg(`📍 Location unavailable. Marking ${action} anyway…`, "info");
        await sendAttendance(null, null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const msgStyle =
    messageType === "success" ? "text-green-700 bg-green-50 border border-green-200" :
    messageType === "error"   ? "text-red-700 bg-red-50 border border-red-200"       :
                                "text-blue-700 bg-blue-50 border border-blue-200";

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 flex flex-col items-center p-4 pt-24">
      <UserDashboard />

      <h2 className="text-3xl font-bold font-mono text-purple-700 mb-1">Attendance System</h2>
      <p className="text-sm text-gray-500 mb-4">
        {worker
          ? <>Logged in as <span className="font-semibold text-purple-600">{worker.username}</span> · {worker.role} · {worker.workMode}</>
          : "No worker logged in"}
      </p>

      {/* Location info — store only, no restriction */}
      <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 max-w-lg text-sm text-blue-700">
        <FaMapMarkerAlt className="shrink-0" />
        <p>Your current location will be recorded when marking attendance.</p>
      </div>

      {/* Detection message */}
      {detectionMessage && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium max-w-lg w-full text-center ${msgStyle}`}>
          {detectionMessage}
        </div>
      )}

      {/* Webcam */}
      <div className="relative border-2 border-purple-300 rounded-xl overflow-hidden shadow-lg mb-3">
        <Webcam ref={webcamRef} style={{ width: 540, height: 410 }} mirrored />
        <canvas ref={canvasRef} style={{ position: "absolute", top: 0, left: 0, width: 540, height: 410 }} />
        {faceMatched && (
          <div className="absolute inset-0 border-4 border-green-400 rounded-xl pointer-events-none" />
        )}
      </div>

      {!isModelsLoaded && (
        <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
          <FaSpinner className="animate-spin" /> {loadingText}
        </p>
      )}

      {/* Scan Face button */}
      <button
        onClick={handleVerifyFace}
        disabled={scanning || !isModelsLoaded}
        className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition mb-4"
      >
        {scanning
          ? <><FaSpinner className="animate-spin" /> Scanning…</>
          : <><MdFaceRetouchingNatural className="text-xl" /> {isModelsLoaded ? "Scan Face" : loadingText}</>}
      </button>

      {/* Login / Logout buttons */}
      {faceMatched && (
        <div className="flex gap-4 mt-1">
          <button
            onClick={() => markAttendance("login")}
            disabled={loadingLocation}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold transition ${
              loadingLocation ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loadingLocation ? <><FaSpinner className="animate-spin" /> Marking…</> : <><FaCheckCircle /> Login</>}
          </button>

          <button
            onClick={() => markAttendance("logout")}
            disabled={loadingLocation}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold transition ${
              loadingLocation ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {loadingLocation ? <><FaSpinner className="animate-spin" /> Marking…</> : <><FaTimesCircle /> Logout</>}
          </button>
        </div>
      )}

      {!faceMatched && isModelsLoaded && (
        <p className="mt-4 text-xs text-gray-400 text-center max-w-sm">
          Look directly at the camera, ensure good lighting, then click <strong>Scan Face</strong>.
        </p>
      )}
    </div>
  );
};

export default WorkerFaceAttendance;